import Flash from 'admin/components/shared/Flash'
import History from 'admin/components/History'
import Email from 'admin/components/shared/Email'
import useBoolState from 'admin/hooks/useBoolState'
import useDocument from 'admin/hooks/useDocument'
import useEffect from 'admin/hooks/useEffect'
import useInvoice from 'admin/hooks/useInvoice'
import useOrganisation from 'admin/hooks/useOrganisation'
import useStateObject from 'admin/hooks/useStateObject'
import {formatNumber, toParams} from 'admin/lib/format'
import moment, {dateIsTodayOrInThePast} from 'admin/lib/moment'
import diff from 'deep-diff'
import React, {Fragment} from 'react'
import {useHistory, useParams} from 'react-router-dom'
import Form from './Form'
import Header from '../shared/Header'
import Deletion from '../Deletion'

const EMAIL_DEFAULTS = {
  subject: `Invoice [Invoice Number] for [Contact Name]`,
  message: `Hi [Contact First Name],

Here's your [Invoice Month] invoice [Invoice Number] for $NZD [Amount], due on [Due Date].

If you have any questions, please let us know.

Ngā mihi,
[Organisation Name]`
}

export default () => {
  const history = useHistory()
  const {organisationID, invoiceID} = useParams()
  const {organisation} = useOrganisation(organisationID)
  const {invoice, errors, update, destroy, send} = useInvoice(organisationID, invoiceID)

  const [form, setForm, resetForm] = useStateObject(invoice, {email: EMAIL_DEFAULTS})
  const [showDeleteConfirmation, setShowDeleteConfirmation, toggleShowDeleteConfirmation] = useBoolState()
  const [showEmailModal, setShowEmailModal, toggleShowEmailModal] = useBoolState()

  useDocument({title: form.invoiceNumber || `Invoice ${invoiceID}`})
  useEffect(() => resetForm(invoice), [invoice], false)
  useEffect(() => errors.email && toggleShowEmailModal(), [errors.email], false)

  const handleApprove = e => {
    e.preventDefault()
    e.target.blur()
    update({state: 'approved'})
  }

  const handleUnapprove = e => {
    e.preventDefault()
    e.target.blur()
    update({state: 'draft'})
  }

  const handleDelete = ({notes}) => {
    destroy({notes}, () => history.replace(`/orgs/${organisationID}/invoices`))
  }

  const handleUpdate = e => {
    e.preventDefault()
    e.target.blur()
    update(form)
  }

  const handleSendEmail = email => {
    send(email, toggleShowEmailModal)
  }

  const handleChange = changes => {
    if (changes.isSendable && !form.isSendable) toggleShowEmailModal()
    setForm(changes)
  }

  const handleChangeEmail = email => {
    setForm({email})
    toggleShowEmailModal()
  }

  const handleCancel = e => {
    e.preventDefault()
    resetForm(invoice)
  }

  const breadcrumbs = []
  if (invoice.account?.id) {
    breadcrumbs.push({label: 'Manage accounts', path: `/orgs/${organisationID}/accounts`})
    breadcrumbs.push({
      label: invoice.account?.displayName,
      path: `/orgs/${organisationID}/accounts/${invoice.account.id}`
    })
  } else {
    breadcrumbs.push({label: 'Invoices', path: `/orgs/${organisationID}/invoices`})
  }

  const ignoredChanges = (path, key) =>
    path.length === 0 && key === 'email' && (!invoice.email?.subject || !invoice.email?.message)
  const isSendableNow = ['approved', 'locked'].includes(invoice.state) && dateIsTodayOrInThePast(form.date)
  const hasChanges = diff(form, invoice, ignoredChanges)

  const total = (invoice.lineItems || []).reduce((sum, item) => sum + item.totalAmount, 0)

  return (
    <Fragment>
      <Flash />

      {showDeleteConfirmation && <Deletion onConfirm={handleDelete} onClose={toggleShowDeleteConfirmation} />}
      {showEmailModal && (
        <Email
          email={form.email}
          errors={errors.email ? errors.email[0] : {}}
          onSend={isSendableNow && handleSendEmail}
          onSave={!isSendableNow && handleChangeEmail}
          onExit={toggleShowEmailModal}
          placeholders={{
            ['Invoice Month']: moment(form.date).format('MMMM'),
            ['Invoice Number']: form.invoiceNumber,
            ['Due Date']: moment(form.dueDate).format('D MMM YYYY'),
            ['Contact First Name']: form.account?.contactFirstName,
            ['Invoice Total With Currency']: formatNumber(total, {thousands: true, currency: true}),
            ['Amount']: formatNumber(total, {thousands: true}),
            ['Organisation Name']: organisation.externalName,
            ['Contact Name']: form.account?.displayName
          }}>
          <h5 className='subtitle is-6'>
            <i className='fa fa-paperclip' aria-hidden='true'></i>
            &nbsp;&nbsp; Attached PDF{' '}
            <a
              href={`/orgs/${organisationID}/downloads/invoice.pdf?${toParams({
                invoice_id: invoiceID,
                inline: true
              })}`}
              target='_blank'>
              {form.state === 'locked' ? 'View' : 'Preview'}
            </a>
          </h5>
        </Email>
      )}
      {form.state === 'locked' && (
        <section className='py-3 section has-background-dark'>
          <div className='container'>
            <span className='icon-text'>
              <span className='icon'>
                <i className='fas fa-lock'></i>
              </span>
              <span className='is-size-7 has-text-light ml-2'>
                This invoice has been {form.state} and cannot be changed.
              </span>
            </span>
          </div>
        </section>
      )}
      <Header
        breadcrumbs={breadcrumbs}
        title={`Edit invoice ${form.invoiceNumber}`}
        subtitle={
          form.xeroLink ? (
            <h5 className='subtitle is-7'>
              <a href={form.xeroLink} target='_blank'>
                <span>Imported from Xero</span>{' '}
                <span className='icon'>
                  <i className='fa fa-external-link'></i>
                </span>
              </a>
            </h5>
          ) : null
        }>
        {hasChanges ? (
          <Fragment>
            <button className='button is-text is-decorationless' onClick={handleCancel}>
              Cancel
            </button>
            <button className='button is-primary' onClick={handleUpdate}>
              Save
            </button>
          </Fragment>
        ) : form.id ? (
          <Fragment>
            <a
              href={`/orgs/${organisationID}/downloads/invoice.pdf?${toParams({
                invoice_id: invoiceID,
                inline: true
              })}`}
              target='_blank'
              className='button is-outlined is-dark'>
              {form.state === 'locked' ? 'View' : 'Preview'} PDF
            </a>
            {isSendableNow ? (
              <button className='button is-outlined is-dark' onClick={toggleShowEmailModal}>
                {form.isSent ? 'Resend' : 'Send'} email
              </button>
            ) : null}
            {['draft', 'approved'].includes(form.state) ? (
              <button className='button is-danger is-outlined' onClick={toggleShowDeleteConfirmation}>
                Delete
              </button>
            ) : null}
            {form.state === 'draft' ? (
              <button className='button is-primary' onClick={handleApprove}>
                Approve
              </button>
            ) : form.state === 'approved' ? (
              <button className='button is-outlined is-info' onClick={handleUnapprove}>
                Revert to Draft
              </button>
            ) : null}
          </Fragment>
        ) : null}
      </Header>
      <section className='section is-narrow'>
        <div className='container'>
          <Form
            errors={errors}
            organisationID={organisationID}
            invoice={form}
            onChange={handleChange}
            onEditEmailTemplate={toggleShowEmailModal}
          />
        </div>
      </section>
      <History itemType='Invoice' itemId={invoiceID} className='has-background-white-bis' />
    </Fragment>
  )
}
